import { useState, useEffect, useRef } from "react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useParams, useLocation, Link } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  WorkOrderWithRelations,
  User as AppUser,
  Company,
  ServiceType,
  CredentialType,
  Contact,
  CarrierWithRelations,
  FmcsaIncidentWithRelations,
  AuthorityLetterWithRelations,
  WatchlistWithRelations,
  WorkOrderStatusHistory,
} from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatDateOnly } from "@/lib/dateUtils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import EditWorkOrderModal from "@/components/modals/edit-work-order-modal";
import { CreateFmcsaIncidentModal } from "@/components/modals/create-fmcsa-incident-modal";
import { EditFmcsaIncidentModal } from "@/components/modals/edit-fmcsa-incident-modal";
import { AuthorityLettersSearchModal } from "@/components/modals/authority-letters-search-modal";
import { WatchlistCreateModal } from "@/components/modals/watchlist-create-modal";
import { WatchlistDetailModal } from "@/components/modals/watchlist-detail-modal";
import PaymentCollectionModal from "@/components/modals/payment-collection-modal";
import CompanyDetails from "@/components/details/company-details";
import CarrierDetails from "@/components/details/carrier-details";
import NotesSection from "@/components/notes/notes-section";
import { useToast } from "@/hooks/use-toast";
import { FileText, Calendar, User, Edit, Truck, Building2, ExternalLink, AlertTriangle, Mail, Phone, ChevronRight, ChevronLeft, ChevronDown, ChevronUp, FileArchive, Plus, Eye, Download, Search, RefreshCw, Receipt, DollarSign, Trash2, Loader2, Lock, History, Layers, ArrowRight, Clock, BookOpen } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useActiveTimeTracker } from "@/hooks/use-active-time-tracker";
import { StripeInvoiceHistoryModal } from "@/components/modals/stripe-invoice-history-modal";
import AuditInfo from "@/components/audit-info";
import { formatPhoneNumber } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { formatWorkOrderStatusChangedBy } from "@shared/workOrderStatusHistory";

interface StatusHistoryEntry extends WorkOrderStatusHistory {
  changedByUser?: { firstName: string | null; lastName: string | null } | null;
}

interface WorkOrderDetails extends WorkOrderWithRelations {
  company: (Company & { dotProfile?: { dotNumber: string | null } | null }) | null;
  requestingContact: (Contact & { companies?: Company[] }) | null;
  secondaryContact: (Contact & { companies?: Company[] }) | null;
  serviceTypeInfo: ServiceType | null;
  createdByUser: AppUser | null;
  updatedByUser: AppUser | null;
  credentialTypes: CredentialType[];
  carrier: CarrierWithRelations | null;
}

function getWatchlistAge(watchlist: {
  createdAt?: string | Date | null;
  completedAt?: string | Date | null;
  canceledAt?: string | Date | null;
  expiredAt?: string | Date | null;
}): number {
  const created = watchlist.createdAt ? new Date(watchlist.createdAt).getTime() : null;
  if (!created) return 0;
  const now = Date.now();
  const end = Math.min(
    watchlist.completedAt ? new Date(watchlist.completedAt).getTime() : now,
    watchlist.canceledAt  ? new Date(watchlist.canceledAt).getTime()  : now,
    watchlist.expiredAt   ? new Date(watchlist.expiredAt).getTime()   : now,
    now
  );
  return Math.round((end - created) / 86_400_000);
}

// Task Item Component
function TaskItem({ task, onToggleComplete }: { task: any; onToggleComplete: (taskId: number, completed: boolean) => void }) {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors" data-testid={`task-${task.id}`}>
      <Checkbox
        checked={task.completed}
        onCheckedChange={(checked) => onToggleComplete(task.id, checked as boolean)}
        className="mt-1"
        data-testid={`checkbox-task-${task.id}`}
      />
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <h4 className={`font-medium ${task.completed ? 'line-through text-gray-500' : ''}`}>
            {task.title}
          </h4>
          <Badge className={task.completed ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>
            {task.completed ? "Completed" : "Pending"}
          </Badge>
        </div>
        {task.description && (
          <p className={`text-sm mt-1 ${task.completed ? 'text-gray-400' : 'text-gray-600'}`}>
            {task.description}
          </p>
        )}
        <div className="flex gap-4 mt-2 text-xs text-gray-500">
          {task.completed && task.completedDate && (
            <span>✓ Completed: {formatDate(task.completedDate)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

const originLabels: Record<string, { label: string; href: string }> = {
  dashboard: { label: "Dashboard", href: "/dashboard" },
};

export default function WorkOrderDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const canEditStatus = currentUser?.role === "admin" || currentUser?.role === "staff";
  
  // Get backTo context from URL query parameter (e.g., ?from=dashboard)
  const urlParams = new URLSearchParams(window.location.search);
  const originParam = urlParams.get("from");
  const backToContext = originParam && originLabels[originParam] ? originLabels[originParam] : undefined;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateIncidentModal, setShowCreateIncidentModal] = useState(false);
  const [showEditIncidentModal, setShowEditIncidentModal] = useState(false);
  const [selectedIncident, setSelectedIncident] = useState<any>(null);
  const [showCompanyDetails, setShowCompanyDetails] = useState(false);
  const [showCarrierDetails, setShowCarrierDetails] = useState(false);
  const [showAuthorityLettersSearch, setShowAuthorityLettersSearch] = useState(false);
  const [showWatchlistCreate, setShowWatchlistCreate] = useState(false);
  const [selectedWatchlist, setSelectedWatchlist] = useState<any>(null);
  const [showWatchlistDetail, setShowWatchlistDetail] = useState(false);
  const [showPaymentCollectionModal, setShowPaymentCollectionModal] = useState(false);
  const [showInvoiceHistory, setShowInvoiceHistory] = useState(false);
  const [detailsCollapsed, setDetailsCollapsed] = useState(false);
  
  const workOrderId = id ? parseInt(id) : 0;
  const trackedWorkOrderRef = useRef<number | null>(null);

  const { data: workOrder, isLoading } = useQuery<WorkOrderDetails>({
    queryKey: [`/api/work-orders/${workOrderId}`],
    enabled: !!workOrderId,
    retry: false,
    refetchOnMount: "always",
  });

  // Passive time tracking (#132). Staff and admins only, and only once the work
  // order has loaded, so a refused or missing order never reports time.
  useActiveTimeTracker(canEditStatus && workOrder ? workOrderId : null);

  const { data: users } = useQuery<AppUser[]>({
    queryKey: ["/api/assignable-users"],
    retry: false,
    refetchOnMount: "always",
  });

  const { data: fmcsaIncidents } = useQuery<FmcsaIncidentWithRelations[]>({
    queryKey: [`/api/work-orders/${workOrderId}/fmcsa-incidents`],
    enabled: !!workOrderId,
    retry: false,
    refetchOnMount: "always",
  });

  const { data: authorityLetters } = useQuery<AuthorityLetterWithRelations[]>({
    queryKey: [`/api/work-orders/${workOrderId}/authority-letters`],
    enabled: !!workOrderId,
    retry: false,
    refetchOnMount: "always",
  });

  const { data: watchlists } = useQuery<WatchlistWithRelations[]>({
    queryKey: [`/api/work-orders/${workOrderId}/watchlists`],
    enabled: !!workOrderId,
    retry: false,
    refetchOnMount: "always",
  });

  const { data: statusHistory } = useQuery<StatusHistoryEntry[]>({
    queryKey: [`/api/work-orders/${workOrderId}/status-history`],
    enabled: !!workOrderId,
    retry: false,
    refetchOnMount: "always",
  });

  const { data: invoiceData, isLoading: isLoadingInvoice } = useQuery<{ total: string; lineItems: any[]; paidWithClientCardTotal: string }>({
    queryKey: [`/api/work-orders/${workOrderId}/invoice-line-items`],
    enabled: !!workOrderId,
    retry: false,
    refetchOnMount: "always",
  });

  // Fetch payment status for this work order
  const { data: paymentStatus, refetch: refetchPayment } = useQuery<{
    id: number;
    status: string;
    stripeInvoiceId: string | null;
    stripeInvoiceNumber: string | null;
    batchInvoiceRunId: number | null;
    amountDue: string | null;
    amountPaid: string | null;
    unpaidBalance: string | null;
    hostedInvoiceUrl: string | null;
    pdfUrl: string | null;
    customerEmail: string | null;
    deliveryChannels: string[] | null;
  } | null>({
    queryKey: [`/api/work-orders/${workOrderId}/payment`],
    enabled: !!workOrderId,
    retry: false,
    refetchOnMount: "always",
  });

  // 'paid' locks for all edits; open statuses block until voided.
  const OPEN_INVOICE_STATUSES = ['pending', 'draft', 'overdue', 'partial'] as const;
  const invoiceIsOpen = OPEN_INVOICE_STATUSES.includes(paymentStatus?.status as any);
  const lineItemsLocked = paymentStatus?.status === 'paid' || invoiceIsOpen;

  const workOrderPageTitle = (() => {
    if (!workOrder) return null;
    const parts = [workOrder.workOrderNumber || `WO-${workOrderId}`];
    if (workOrder.serviceType) parts.push(workOrder.serviceType);
    if (workOrder.company?.name) parts.push(workOrder.company.name);
    const identifiers: string[] = [];
    if (workOrder.carrier?.docketPrefix && workOrder.carrier?.docketNumber) {
      identifiers.push(`Docket:${workOrder.carrier.docketPrefix}${workOrder.carrier.docketNumber}`);
    }
    if (workOrder.company?.dotProfile?.dotNumber) {
      identifiers.push(`DOT:${workOrder.company.dotProfile.dotNumber}`);
    }
    if (identifiers.length > 0) parts.push(`(${identifiers.join(' ')})`);
    return parts.join(' - ');
  })();
  usePageTitle(workOrderPageTitle);

  useEffect(() => {
    if (!workOrder || trackedWorkOrderRef.current === workOrderId) return;
    trackedWorkOrderRef.current = workOrderId;

    track("work_order_details_viewed", {
      source_page: `/work-orders/${workOrderId}`,
      work_order_id: workOrderId,
      company_id: workOrder.companyId ?? null,
      carrier_id: workOrder.carrierId ?? null,
    });
  }, [workOrder, workOrderId]);

  const updateWorkOrderMutation = useMutation({
    mutationFn: async (workOrderData: any) => {
      await apiRequest("PUT", `/api/work-orders/${workOrderId}`, workOrderData);
    },
    onSuccess: (_data, variables) => {
      track("work_order_updated", {
        source_page: `/work-orders/${workOrderId}`,
        work_order_id: workOrderId,
        company_id: workOrder?.companyId ?? variables?.companyId ?? null,
        carrier_id: workOrder?.carrierId ?? variables?.carrierId ?? null,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}`] });
      // If service type changed, also refresh invoice line items immediately
      if (variables?.serviceType && variables.serviceType !== workOrder?.serviceType) {
        queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/invoice-line-items`] });
      }
      toast({
        title: "Success",
        description: "Work order updated successfully",
      });
      setShowEditModal(false);
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update work order",
        variant: "destructive",
      });
    },
  });

  const updateTaskMutation = useMutation({
    mutationFn: async ({ taskId, completed }: { taskId: number; completed: boolean }) => {
      await apiRequest("PUT", `/api/tasks/${taskId}`, { completed });
    },
    onSuccess: (_data, variables) => {
      if (variables.completed) {
        track("task_completed", {
          source_page: `/work-orders/${workOrderId}`,
          work_order_id: workOrderId,
          company_id: workOrder?.companyId ?? null,
          carrier_id: workOrder?.carrierId ?? null,
          task_id: variables.taskId,
          completed: true,
        });
      }
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}`] });
      toast({
        title: "Success",
        description: "Task updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update task",
        variant: "destructive",
      });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (status: string) => {
      await apiRequest("PUT", `/api/work-orders/${workOrderId}`, { status });
    },
    onSuccess: (_data, status) => {
      track("work_order_status_changed", {
        source_page: `/work-orders/${workOrderId}`,
        work_order_id: workOrderId,
        company_id: workOrder?.companyId ?? null,
        carrier_id: workOrder?.carrierId ?? null,
        status,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}`] });
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/status-history`] });
      toast({
        title: "Success",
        description: "Status updated successfully",
      });
    },
    onError: (error: unknown) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to update status",
        variant: "destructive",
      });
    },
  });

  const updateAssignedUserMutation = useMutation({
    mutationFn: async (assignedToUserId: string | null) => {
      await apiRequest("PUT", `/api/work-orders/${workOrderId}`, { assignedToUserId });
    },
    onSuccess: (_data, assignedToUserId) => {
      track("work_order_assignee_changed", {
        source_page: `/work-orders/${workOrderId}`,
        work_order_id: workOrderId,
        company_id: workOrder?.companyId ?? null,
        carrier_id: workOrder?.carrierId ?? null,
        assigned_to_user_id: assignedToUserId,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}`] });
      toast({
        title: "Success",
        description: "Assigned user updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update assigned user",
        variant: "destructive",
      });
    },
  });

  const updateIncidentStatusMutation = useMutation({
    mutationFn: async ({ incidentId, status }: { incidentId: number; status: string }) => {
      await apiRequest("PUT", `/api/fmcsa-incidents/${incidentId}`, { status });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/fmcsa-incidents`] });
      queryClient.invalidateQueries({ queryKey: ["/api/fmcsa-incidents"] });
      toast({
        title: "Success",
        description: "Incident status updated successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update incident status",
        variant: "destructive",
      });
    },
  });

  const createDefaultInvoiceItemsMutation = useMutation({
    mutationFn: async () => {
      if (!workOrder?.serviceType || !workOrder?.companyId) {
        throw new Error("Missing service type or company");
      }
      await apiRequest("POST", `/api/work-orders/${workOrderId}/invoice-line-items/default`, {
        serviceTypeId: workOrder.serviceType,
        companyId: workOrder.companyId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/invoice-line-items`] });
      toast({
        title: "Success",
        description: "Invoice items created successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create invoice items",
        variant: "destructive",
      });
    },
  });

  const deleteInvoiceItemMutation = useMutation({
    mutationFn: async (itemId: number) => {
      await apiRequest("DELETE", `/api/invoice-line-items/${itemId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/invoice-line-items`] });
      toast({
        title: "Success",
        description: "Invoice item removed",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to remove invoice item",
        variant: "destructive",
      });
    },
  });

  const updateInvoiceItemMutation = useMutation({
    mutationFn: async ({ itemId, updates }: { itemId: number; updates: any }) => {
      await apiRequest("PUT", `/api/invoice-line-items/${itemId}`, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/invoice-line-items`] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to update invoice item",
        variant: "destructive",
      });
    },
  });

  const voidInvoiceMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/work-orders/${workOrderId}/void-invoice`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/payment`] });
      toast({
        title: "Success",
        description: "Invoice has been voided",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to void invoice",
        variant: "destructive",
      });
    },
  });

  const resendInvoiceMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/work-orders/${workOrderId}/resend-invoice`);
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Invoice has been resent",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to resend invoice",
        variant: "destructive",
      });
    },
  });

  const syncPaymentMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/work-orders/${workOrderId}/sync-payment`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/payment`] });
      toast({
        title: "Success",
        description: "Payment status synced from Stripe",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to sync payment status",
        variant: "destructive",
      });
    },
  });

  const sendWatchlistEmailMutation = useMutation({
    mutationFn: ({ watchlistId, emailType }: { watchlistId: number; emailType: 'reminder' | 'dismissal' }) =>
      apiRequest("POST", `/api/watchlists/${watchlistId}/send-email`, { emailType }).then(r => r.json()),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/watchlists`] });
      const sent = data?.sentCount ?? 0;
      const blocked = data?.blockedCount ?? 0;
      if (sent > 0) {
        toast({ title: "Email sent", description: `Sent to ${sent} recipient(s).` });
      } else if (blocked > 0) {
        toast({ title: "Email blocked (test mode)", description: `Blocked for ${blocked} recipient(s) — domain not in allowed list.`, variant: "destructive" });
      } else {
        toast({ title: "No recipients", description: "No contacts with email addresses found on this work order.", variant: "destructive" });
      }
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to send watchlist email.", variant: "destructive" });
    },
  });

  const handleUpdateWorkOrder = (workOrderData: any) => {
    updateWorkOrderMutation.mutate(workOrderData);
  };

  const handleViewProfile = (docketNumber: string) => {
    if (docketNumber) {
      const profileUrl = `https://safer.fmcsa.dot.gov/query.asp?searchtype=ANY&query_type=queryCarrierSnapshot&query_param=MC_MX&query_string=${docketNumber}`;
      window.open(profileUrl, "_blank");
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      // Client-action group (amber): client needs to act
      case "collecting_inputs":
      case "awaiting_payment":
      case "awaiting_signatures":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300";
      // Internal group (blue): DNA staff working
      case "new":
      case "validating":
      case "preparing_documents":
      case "work_complete":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "awaiting_client_filing":
        return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300";
      // External group (purple): submitted to third parties
      case "submitted":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      // Terminal group (gray): closed or cancelled
      case "closed":
      case "cancelled":
        return "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400";
      // Legacy statuses
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "in_progress":
        return "bg-amber-100 text-amber-800";
      case "awaiting_documents":
        return "bg-amber-100 text-amber-800";
      case "pending":
        return "bg-amber-100 text-amber-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatServiceType = (serviceType: string) => {
    return serviceType.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  if (isLoading) {
    return (
      <ResponsiveLayout title="Work Order Details" subtitle="Loading..." currentPage="work-orders">
        <div className="flex items-center justify-center py-8">
          <p className="text-gray-500">Loading work order details...</p>
        </div>
      </ResponsiveLayout>
    );
  }

  if (!workOrder || Object.keys(workOrder).length === 0) {
    return (
      <ResponsiveLayout title="Work Order Details" subtitle="Not found" currentPage="work-orders">
        <div className="flex items-center justify-center py-8">
          <p className="text-gray-500">Work order not found</p>
        </div>
      </ResponsiveLayout>
    );
  }

  const completedTasks = workOrder.tasks?.filter((t: any) => t.completed).length || 0;
  const totalTasks = workOrder.tasks?.length || 0;

  return (
    <ResponsiveLayout 
      title={workOrder.workOrderNumber} 
      subtitle="Work Order Details" 
      currentPage="work-orders"
      breadcrumbs={[
        { label: "Work Orders", href: "/work-orders" },
        { label: workOrder.workOrderNumber || "Work Order" }
      ]}
      backTo={backToContext}
    >
      <div className="space-y-4">
        {/* Condensed Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Row 1: Work Order # and Status */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <FileText className="w-6 h-6 text-primary shrink-0" />
                  <div className="min-w-0">
                    <h1 className="text-2xl font-bold whitespace-nowrap" data-testid="text-work-order-number">
                      {[
                        workOrder.workOrderNumber,
                        workOrder.company?.name,
                        (workOrder.carrier?.company?.dotProfile?.dotNumber || workOrder.company?.dotProfile?.dotNumber)
                          ? `DOT:${workOrder.carrier?.company?.dotProfile?.dotNumber || workOrder.company?.dotProfile?.dotNumber}`
                          : null,
                        (workOrder.carrier?.docketPrefix && workOrder.carrier?.docketNumber)
                          ? `${workOrder.carrier.docketPrefix}${workOrder.carrier.docketNumber}`
                          : null,
                      ].filter(Boolean).join(" - ")}
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-base font-semibold text-primary">{workOrder.serviceTypeInfo?.name || formatServiceType(workOrder.serviceType)}</p>
                      {paymentStatus && (
                        <Badge
                          className={`text-sm border-0 ${
                            paymentStatus.status === 'paid' ? 'bg-green-100 text-green-800' :
                            paymentStatus.status === 'partial' ? 'bg-amber-100 text-amber-800' :
                            paymentStatus.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                            paymentStatus.status === 'overdue' ? 'bg-red-100 text-red-800' :
                            paymentStatus.status === 'voided' ? 'bg-gray-100 text-gray-700' :
                            paymentStatus.status === 'uncollectible' ? 'bg-red-100 text-red-800' :
                            paymentStatus.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                            'bg-blue-100 text-blue-800'
                          }`}
                          data-testid="badge-payment-status-header"
                        >
                          {paymentStatus.status === 'paid' ? 'Paid' :
                           paymentStatus.status === 'partial' ? 'Partial Payment' :
                           paymentStatus.status === 'pending' ? 'Invoiced' :
                           paymentStatus.status === 'overdue' ? 'Overdue' :
                           paymentStatus.status === 'voided' ? 'Voided' :
                           paymentStatus.status === 'uncollectible' ? 'Uncollectible' :
                           paymentStatus.status === 'draft' ? 'Draft Invoice' :
                           paymentStatus.status.charAt(0).toUpperCase() + paymentStatus.status.slice(1)}
                        </Badge>
                      )}
                    </div>
                    <AuditInfo
                      createdAt={workOrder.createdAt}
                      createdBy={workOrder.createdByUser}
                      updatedAt={workOrder.updatedAt}
                      updatedBy={workOrder.updatedByUser}
                      className="mt-1"
                    />
                    {workOrder.credentialTypes && workOrder.credentialTypes.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        <span className="text-xs text-gray-500 mr-1">Credentials:</span>
                        {workOrder.credentialTypes.map((ct: any) => (
                          <Badge 
                            key={ct.id} 
                            variant="outline" 
                            className="text-xs bg-blue-50 text-blue-700 border-blue-200"
                          >
                            {ct.name}
                          </Badge>
                        ))}
                      </div>
                    )}
                    {workOrder.description && (
                      <p className="text-base text-gray-700 mt-2">
                        <span className="font-medium">Description:</span> {workOrder.description}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Assigned:</label>
                    <Select 
                      value={workOrder.assignedToUserId || "unassigned"} 
                      onValueChange={(value) => updateAssignedUserMutation.mutate(value === "unassigned" ? null : value)}
                      data-testid="select-assigned-user"
                    >
                      <SelectTrigger className="w-48">
                        <SelectValue placeholder="Assign to..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {users?.map((user: any) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.firstName} {user.lastName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Status:</label>
                    {canEditStatus ? (
                      <Select 
                        value={workOrder.status} 
                        onValueChange={(value) => updateStatusMutation.mutate(value)}
                        data-testid="select-status"
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue>
                            <Badge className={`${getStatusColor(workOrder.status)} border-0`}>
                              {formatStatus(workOrder.status)}
                            </Badge>
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="collecting_inputs">Collecting Inputs</SelectItem>
                          <SelectItem value="validating">Validating</SelectItem>
                          <SelectItem value="awaiting_payment">Awaiting Payment</SelectItem>
                          <SelectItem value="preparing_documents">Preparing Documents</SelectItem>
                          <SelectItem value="awaiting_signatures">Awaiting Signatures</SelectItem>
                          <SelectItem value="submitted">Submitted</SelectItem>
                          <SelectItem value="awaiting_client_filing">Awaiting Client Filing</SelectItem>
                          <SelectItem value="work_complete">Work Complete</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`${getStatusColor(workOrder.status)} border-0`} data-testid="badge-status">
                        {formatStatus(workOrder.status)}
                      </Badge>
                    )}
                    {(() => {
                      const lastChange = statusHistory?.[0];
                      if (!lastChange) return null;
                      const rawTs = lastChange.changedAt as unknown as string;
                      const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(rawTs);
                      const since = new Date(hasTimezone ? rawTs : rawTs + 'Z');
                      if (isNaN(since.getTime())) return null;
                      const days = Math.floor((Date.now() - since.getTime()) / 86_400_000);
                      if (days <= 0) return null;
                      return (
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {days}d in {formatStatus(workOrder.status)}
                        </span>
                      );
                    })()}
                  </div>
                  {(currentUser?.role === "admin" || currentUser?.role === "staff") && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!workOrder.serviceTypeInfo?.sopUrl}
                      onClick={() => {
                        if (workOrder.serviceTypeInfo?.sopUrl) {
                          window.open(workOrder.serviceTypeInfo.sopUrl, "_blank", "noopener,noreferrer");
                        }
                      }}
                      className={!workOrder.serviceTypeInfo?.sopUrl ? "opacity-50 cursor-not-allowed" : ""}
                      title={workOrder.serviceTypeInfo?.sopUrl ? "View standard operating procedure" : "No SOP link configured for this service"}
                    >
                      <BookOpen className="w-4 h-4 mr-2" />
                      View SOP
                    </Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setShowEditModal(true)} data-testid="button-edit">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                </div>
              </div>

            </div>
          </CardContent>
        </Card>

        {/* Main Content Area with Resizable Panels */}
        <ResizablePanelGroup direction="horizontal" className="min-h-[500px]">
          {/* Tasks Section */}
          <ResizablePanel defaultSize={35} minSize={20}>
            <Card className="h-full">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    Tasks
                    <Badge variant="outline">
                      {completedTasks} / {totalTasks} Complete
                    </Badge>
                  </CardTitle>
                  {workOrder.assignedToUser && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <User className="w-4 h-4" />
                      <span>Assigned to: {workOrder.assignedToUser.firstName} {workOrder.assignedToUser.lastName}</span>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {workOrder.tasks && workOrder.tasks.length > 0 ? (
                  <div className="space-y-2">
                    {workOrder.tasks.map((task: any) => (
                      <TaskItem 
                        key={task.id} 
                        task={task} 
                        onToggleComplete={(taskId: number, completed: boolean) => {
                          updateTaskMutation.mutate({ taskId, completed });
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-8">No tasks found</p>
                )}
              </CardContent>
            </Card>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Notes + FMCSA Incidents Panel */}
          <ResizablePanel defaultSize={40} minSize={20}>
            <div className="flex flex-col gap-3 h-full overflow-y-auto">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Notes</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <NotesSection
                    entityType="work_order"
                    entityId={workOrderId.toString()}
                    title=""
                  />
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      FMCSA Incident Records
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCreateIncidentModal(true)}
                      data-testid="button-create-incident"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Create Incident
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {fmcsaIncidents && fmcsaIncidents.length > 0 ? (
                    <div className="space-y-2">
                      {fmcsaIncidents.map((incident: any) => (
                        <div
                          key={incident.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                          data-testid={`incident-${incident.id}`}
                          onClick={() => {
                            setSelectedIncident(incident);
                            setShowEditIncidentModal(true);
                          }}
                        >
                          <div className="flex items-center gap-3 flex-1">
                            <AlertTriangle className={`w-5 h-5 ${incident.status === 'Resolved' ? 'text-green-500' : 'text-amber-500'}`} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium">{incident.incidentNumber}</p>
                                {incident.expedited && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                                    Expedited
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">
                                {incident.company?.name || 'Unknown Company'}
                                {incident.notes && ` • ${incident.notes.substring(0, 50)}${incident.notes.length > 50 ? '...' : ''}`}
                              </p>
                            </div>
                          </div>
                          <div onClick={(e) => e.stopPropagation()} data-testid={`select-incident-status-${incident.id}`}>
                            <Select
                              value={incident.status}
                              onValueChange={(value) => {
                                updateIncidentStatusMutation.mutate({ incidentId: incident.id, status: value });
                              }}
                            >
                              <SelectTrigger className="w-36">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Unresolved">Unresolved</SelectItem>
                                <SelectItem value="Resolved">Resolved</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No FMCSA incidents linked to this work order.</p>
                      <p className="text-sm mt-2">Click "Create Incident" to add one.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle />

          {/* Details Panel */}
          <ResizablePanel defaultSize={25} minSize={20}>
            <Card className={detailsCollapsed ? "" : "h-full"}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Details</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => setDetailsCollapsed(!detailsCollapsed)}
                    aria-label={detailsCollapsed ? "Expand details" : "Collapse details"}
                  >
                    {detailsCollapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                  </Button>
                </div>
              </CardHeader>
              {!detailsCollapsed && (
                <CardContent className="pt-0 space-y-3">
                  {/* Company */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Company</p>
                    <button
                      onClick={() => setShowCompanyDetails(true)}
                      className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                      data-testid="button-view-company"
                    >
                      <Building2 className="w-4 h-4 text-primary shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-primary text-sm truncate">{workOrder.company?.name || "Unknown"}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {workOrder.company?.dotProfile?.dotNumber ? `DOT: ${workOrder.company.dotProfile.dotNumber}` : "Company"}
                          {workOrder.company?.ein ? ` • EIN: ${workOrder.company.ein}` : ""}
                        </p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                    </button>
                  </div>

                  {/* Carrier */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Carrier</p>
                    {workOrder.carrier ? (
                      <button
                        onClick={() => setShowCarrierDetails(true)}
                        className="w-full flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left"
                        data-testid="button-view-carrier"
                      >
                        <Truck className="w-4 h-4 text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-primary text-sm truncate">{workOrder.carrier.name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {workOrder.carrier.docketNumber ? `${workOrder.carrier.docketPrefix}-${workOrder.carrier.docketNumber}` : "Carrier"}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
                      </button>
                    ) : (
                      <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                        <Truck className="w-4 h-4 text-gray-400 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-500">No Carrier Assigned</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Requesting Contact */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Requesting Contact</p>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      {workOrder.requestingContact ? (
                        <div className="space-y-1">
                          <Link href={`/contacts/${workOrder.requestingContact.id}`} className="font-medium text-primary hover:underline text-sm">
                            {workOrder.requestingContact.firstName} {workOrder.requestingContact.lastName}
                          </Link>
                          {workOrder.requestingContact.email && (
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                              <Mail className="w-3 h-3 shrink-0" />
                              <span className="truncate">{workOrder.requestingContact.email}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <Phone className="w-3 h-3 shrink-0" />
                            {workOrder.requestingContact.phone
                              ? <span>{formatPhoneNumber(workOrder.requestingContact.phone)}</span>
                              : <span className="text-gray-400">Not provided</span>
                            }
                          </div>
                          {(() => {
                            const serviceBureauCompany = workOrder.requestingContact.companies?.find((c: any) => c.type === 'service_bureau');
                            return serviceBureauCompany ? (
                              <div className="flex items-center gap-1 mt-1">
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200" data-testid="badge-service-bureau-requesting">
                                  Service Bureau
                                </Badge>
                                <span className="text-xs text-gray-500 truncate" title={serviceBureauCompany.name}>
                                  {serviceBureauCompany.name}
                                </span>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Not specified</p>
                      )}
                    </div>
                  </div>

                  {/* Secondary Contact */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Secondary Contact</p>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      {workOrder.secondaryContact ? (
                        <div className="space-y-1">
                          <Link href={`/contacts/${workOrder.secondaryContact.id}`} className="font-medium text-primary hover:underline text-sm">
                            {workOrder.secondaryContact.firstName} {workOrder.secondaryContact.lastName}
                          </Link>
                          {workOrder.secondaryContact.email && (
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                              <Mail className="w-3 h-3 shrink-0" />
                              <span className="truncate">{workOrder.secondaryContact.email}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1 text-xs text-gray-600">
                            <Phone className="w-3 h-3 shrink-0" />
                            {workOrder.secondaryContact.phone
                              ? <span>{formatPhoneNumber(workOrder.secondaryContact.phone)}</span>
                              : <span className="text-gray-400">Not provided</span>
                            }
                          </div>
                          {(() => {
                            const serviceBureauCompany = workOrder.secondaryContact.companies?.find((c: any) => c.type === 'service_bureau');
                            return serviceBureauCompany ? (
                              <div className="flex items-center gap-1 mt-1">
                                <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200" data-testid="badge-service-bureau-secondary">
                                  Service Bureau
                                </Badge>
                                <span className="text-xs text-gray-500 truncate" title={serviceBureauCompany.name}>
                                  {serviceBureauCompany.name}
                                </span>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Not assigned</p>
                      )}
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          </ResizablePanel>
        </ResizablePanelGroup>

        {/* Watchlists + Authority Letters — 50/50 grid row */}
        <div className="grid grid-cols-2 gap-4">
          {/* Watchlists */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Active Watchlists
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowWatchlistCreate(true)}
                  data-testid="button-create-watchlist"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Watchlist
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {watchlists && watchlists.length > 0 ? (
                <div className="space-y-2">
                  {watchlists.map((watchlist: any) => (
                    <div
                      key={watchlist.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedWatchlist(watchlist);
                        setShowWatchlistDetail(true);
                      }}
                      data-testid={`watchlist-${watchlist.id}`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <Eye className={`w-5 h-5 ${
                          watchlist.status === 'matched' ? 'text-green-500' :
                          watchlist.status === 'completed' ? 'text-blue-500' :
                          watchlist.status === 'expired' ? 'text-gray-400' :
                          watchlist.status === 'canceled' ? 'text-red-500' :
                          'text-amber-500'
                        }`} />
                        <div className="flex-1">
                          <p className="font-medium">{watchlist.targetIdentifier}</p>
                          <p className="text-sm text-gray-600">
                            {watchlist.letterType && `${watchlist.letterType} • `}
                            {watchlist.watchlistType === 'single' ? 'Single Match' : 'Multiple Matches'}
                            {watchlist.watchlistDays && ` • ${watchlist.watchlistDays} days`}
                            {` • ${getWatchlistAge(watchlist)} d`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          watchlist.status === 'matched' ? 'bg-green-100 text-green-800' :
                          watchlist.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                          watchlist.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                          watchlist.status === 'canceled' ? 'bg-red-100 text-red-800' :
                          'bg-amber-100 text-amber-800'
                        }>
                          {watchlist.status.charAt(0).toUpperCase() + watchlist.status.slice(1)}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title={['canceled', 'expired'].includes(watchlist.status) ? 'Send dismissal email' : 'Send reminder email'}
                          onClick={(e) => {
                            e.stopPropagation();
                            const emailType = ['canceled', 'expired'].includes(watchlist.status)
                              ? 'dismissal'
                              : 'reminder';
                            sendWatchlistEmailMutation.mutate({ watchlistId: watchlist.id, emailType });
                          }}
                          disabled={sendWatchlistEmailMutation.isPending}
                          data-testid={`btn-send-watchlist-email-${watchlist.id}`}
                        >
                          {sendWatchlistEmailMutation.isPending ? (
                            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                          ) : (
                            <Mail className="h-4 w-4 text-blue-500" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Eye className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No active watchlists for this work order.</p>
                  <p className="text-sm mt-2">Click "Create Watchlist" to monitor for new authority letters.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Authority Letters */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Authority Letters
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAuthorityLettersSearch(true)}
                  data-testid="button-search-authority-letters"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Search Letters
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {authorityLetters && authorityLetters.length > 0 ? (
                <div className="space-y-2">
                  {authorityLetters.map((letter: any) => {
                    const getLetterTypeColor = (type: string | null) => {
                      switch (type) {
                        case 'Authority Grant': return 'bg-green-100 text-green-800 border-green-200';
                        case 'Reinstatement': return 'bg-blue-100 text-blue-800 border-blue-200';
                        case 'Name Change': return 'bg-purple-100 text-purple-800 border-purple-200';
                        case 'Transfer': return 'bg-amber-100 text-amber-800 border-amber-200';
                        case 'Mexican Carrier': return 'bg-orange-100 text-orange-800 border-orange-200';
                        default: return 'bg-gray-100 text-gray-800 border-gray-200';
                      }
                    };

                    const getAuthorityTypeDescription = (code: string | null) => {
                      if (!code) return null;
                      const descriptions: Record<string, string> = {
                        'A': 'Common Authority',
                        'B': 'Contract Authority',
                        'C': 'Broker Authority',
                        'E': 'Freight Forwarder',
                        'X': 'Cargo Tank',
                      };
                      return descriptions[code] || code;
                    };

                    return (
                      <div
                        key={letter.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
                        data-testid={`authority-letter-${letter.id}`}
                      >
                        <div className="flex items-center gap-3 flex-1">
                          <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge className={`${getLetterTypeColor(letter.letterType)} text-xs font-medium`}>
                                {letter.letterType || 'Unknown Type'}
                              </Badge>
                              {letter.authorityType && (
                                <span className="text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 px-2 py-0.5 rounded">
                                  {getAuthorityTypeDescription(letter.authorityType)}
                                </span>
                              )}
                              {letter.serviceDate && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {formatDateOnly(letter.serviceDate, "MMM d, yyyy")}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 truncate">
                              {letter.docketFull || (letter.docketPrefix && letter.docketNumber ? `${letter.docketPrefix}-${letter.docketNumber}` : 'Unknown Docket')}
                              {letter.companyName && ` • ${letter.companyName}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          {!letter.letterType && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                try {
                                  await apiRequest('POST', `/api/authority-letters/${letter.id}/backfill`);
                                  queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/authority-letters`] });
                                  toast({
                                    title: "Success",
                                    description: "Authority letter data refreshed from Helix",
                                  });
                                } catch (error) {
                                  toast({
                                    title: "Error",
                                    description: "Failed to refresh authority letter data",
                                    variant: "destructive",
                                  });
                                }
                              }}
                              data-testid={`button-refresh-letter-${letter.id}`}
                            >
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              window.open(`/api/authority-letters/${letter.id}/download`, '_blank');
                            }}
                            data-testid={`button-download-letter-${letter.id}`}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            Download
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No authority letters saved for this work order.</p>
                  <p className="text-sm mt-2">Click "Search Letters" to find and save authority letters.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Invoice Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex flex-col gap-1">
                <CardTitle className="flex items-center gap-2 flex-wrap">
                  <Receipt className="w-5 h-5" />
                  Invoice
                  {paymentStatus?.stripeInvoiceNumber && (
                    <span className="text-sm font-normal text-gray-600" data-testid="text-invoice-number">
                      #{paymentStatus.stripeInvoiceNumber}
                    </span>
                  )}
                  {paymentStatus?.status === 'paid' && (
                    <span className="flex items-center gap-1 text-xs font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-0.5">
                      <Lock className="w-3 h-3" />
                      Locked — invoice paid
                    </span>
                  )}
                  {invoiceIsOpen && (
                    <span className="flex items-center gap-1 text-xs font-normal text-orange-700 bg-orange-50 border border-orange-200 rounded-md px-2 py-0.5">
                      <Lock className="w-3 h-3" />
                      Invoice is open — void it to edit line items
                    </span>
                  )}
                  {paymentStatus && (
                    <Badge 
                      className={
                        paymentStatus.status === 'paid' ? 'bg-green-100 text-green-800' :
                        paymentStatus.status === 'partial' ? 'bg-amber-100 text-amber-800' :
                        paymentStatus.status === 'pending' ? 'bg-blue-100 text-blue-800' :
                        paymentStatus.status === 'overdue' ? 'bg-red-100 text-red-800' :
                        paymentStatus.status === 'voided' ? 'bg-gray-100 text-gray-800' :
                        paymentStatus.status === 'uncollectible' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }
                      data-testid="badge-payment-status"
                    >
                      {paymentStatus.status === 'paid' ? 'Paid' :
                       paymentStatus.status === 'partial' ? 'Partial Payment' :
                       paymentStatus.status === 'pending' ? 'Payment Pending' :
                       paymentStatus.status === 'overdue' ? 'Overdue' :
                       paymentStatus.status === 'voided' ? 'Voided' :
                       paymentStatus.status === 'uncollectible' ? 'Uncollectible' :
                       paymentStatus.status === 'draft' ? 'Draft' :
                       paymentStatus.status.charAt(0).toUpperCase() + paymentStatus.status.slice(1)}
                    </Badge>
                  )}
                  {paymentStatus?.batchInvoiceRunId != null && (
                    <Badge variant="outline" className="gap-1 text-xs font-normal" data-testid="badge-batch-invoice">
                      <Layers className="w-3 h-3" />
                      Batch #{paymentStatus.batchInvoiceRunId}
                    </Badge>
                  )}
                </CardTitle>
                {paymentStatus?.customerEmail && (
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Mail className="w-3 h-3" />
                    Sent to: {paymentStatus.customerEmail}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {paymentStatus?.hostedInvoiceUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(paymentStatus.hostedInvoiceUrl!, '_blank')}
                    data-testid="button-view-stripe-invoice"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Invoice
                  </Button>
                )}
                {paymentStatus?.pdfUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.open(paymentStatus.pdfUrl!, '_blank')}
                    data-testid="button-download-pdf"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                )}
                {paymentStatus?.stripeInvoiceId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => syncPaymentMutation.mutate()}
                    disabled={syncPaymentMutation.isPending}
                    data-testid="button-sync-payment"
                  >
                    {syncPaymentMutation.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Sync
                  </Button>
                )}
                {paymentStatus?.stripeInvoiceId && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowInvoiceHistory(true)}
                    data-testid="button-invoice-history"
                  >
                    <History className="w-4 h-4 mr-2" />
                    History
                  </Button>
                )}
                {paymentStatus && paymentStatus.stripeInvoiceId && 
                 !['paid', 'voided'].includes(paymentStatus.status) && (
                  <>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => resendInvoiceMutation.mutate()}
                      disabled={resendInvoiceMutation.isPending}
                      data-testid="button-resend-invoice"
                    >
                      {resendInvoiceMutation.isPending ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Mail className="w-4 h-4 mr-2" />
                      )}
                      Resend
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        if (confirm('Are you sure you want to void this invoice? This action cannot be undone.')) {
                          voidInvoiceMutation.mutate();
                        }
                      }}
                      disabled={voidInvoiceMutation.isPending}
                      data-testid="button-void-invoice"
                    >
                      {voidInvoiceMutation.isPending ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4 mr-2" />
                      )}
                      Void
                    </Button>
                  </>
                )}
                {invoiceData?.lineItems && invoiceData.lineItems.length > 0 && 
                 (!paymentStatus || !['paid', 'pending', 'partial'].includes(paymentStatus.status)) && (
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={() => setShowPaymentCollectionModal(true)}
                    data-testid="button-collect-payment"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Collect Payment
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingInvoice ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : invoiceData?.lineItems && invoiceData.lineItems.length > 0 ? (
              <div className="space-y-4">
                {/* Table Header */}
                <div className="hidden md:grid md:grid-cols-12 gap-3 px-4 py-2 bg-gray-100 rounded-lg text-xs font-medium text-gray-600 uppercase tracking-wide">
                  <div className="col-span-4">Description</div>
                  <div className="col-span-2 text-right">Amount</div>
                  <div className="col-span-1 text-center">Qty</div>
                  <div className="col-span-2 text-right">Total</div>
                  <div className="col-span-2 text-center">Charge Type</div>
                  <div className="col-span-1"></div>
                </div>

                {/* Invoice Line Items */}
                <div className="space-y-2">
                  {invoiceData.lineItems.map((item: any) => (
                    <div 
                      key={`${item.id}-${item.unitAmount}-${item.quantity}-${item.clientComments || ''}-${item.chargeType}`} 
                      className={`rounded-lg border transition-colors ${
                        item.chargeType === 'paid_with_client_card' ? 'bg-amber-50/50 border-amber-200' : 
                        item.chargeType === 'not_applicable' ? 'bg-gray-50/50 border-gray-200 opacity-60' : 
                        'bg-white border-gray-200 hover:border-gray-300'
                      }`}
                      data-testid={`invoice-item-${item.id}`}
                    >
                      {/* Main Row - Horizontal Layout */}
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 p-4 items-center">
                        {/* Description Column */}
                        <div className="col-span-4 flex items-center gap-3">
                          <div className={`p-1.5 rounded ${item.lineType === 'service_fee' ? 'bg-green-100' : 'bg-blue-100'}`}>
                            {item.lineType === 'service_fee' ? (
                              <DollarSign className="w-4 h-4 text-green-600" />
                            ) : (
                              <Receipt className="w-4 h-4 text-blue-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-gray-900 truncate">{item.description}</span>
                              <Badge 
                                variant={item.lineType === 'service_fee' ? 'default' : 'secondary'} 
                                className="text-[10px] px-1.5 py-0"
                              >
                                {item.lineType === 'service_fee' ? 'Service' : 'Pass-Through'}
                              </Badge>
                            </div>
                            {item.staffNotes && (
                              <p className="text-xs text-gray-500 mt-0.5 truncate" title={item.staffNotes}>
                                {item.staffNotes}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Amount Column */}
                        <div className="col-span-2 flex items-center justify-end">
                          {canEditStatus ? (
                            <div className="relative">
                              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                defaultValue={item.unitAmount || ""}
                                disabled={lineItemsLocked}
                                onBlur={(e) => {
                                  // Drop a typed "$", commas and spaces; the server rejects anything
                                  // that still isn't a plain decimal (DOTTIE-BACKEND-1E).
                                  const cleaned = e.target.value.replace(/[$,\s]/g, "");
                                  if (cleaned !== e.target.value) e.target.value = cleaned;
                                  const newValue = cleaned || null;
                                  if (newValue !== item.unitAmount) {
                                    updateInvoiceItemMutation.mutate({
                                      itemId: item.id,
                                      updates: { unitAmount: newValue }
                                    });
                                  }
                                }}
                                className="w-24 h-9 pl-6 pr-2 text-sm font-semibold text-right bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                                placeholder="0.00"
                                data-testid={`input-amount-${item.id}`}
                              />
                            </div>
                          ) : (
                            <span className="text-sm font-semibold text-gray-900">
                              {item.unitAmount ? `$${item.unitAmount}` : '—'}
                            </span>
                          )}
                        </div>

                        {/* Quantity Column */}
                        <div className="col-span-1 flex items-center justify-center">
                          {canEditStatus ? (
                            <input
                              type="text"
                              inputMode="numeric"
                              defaultValue={item.quantity ?? 0}
                              disabled={item.chargeType === 'not_applicable' || lineItemsLocked}
                              onBlur={(e) => {
                                const parsed = parseInt(e.target.value);
                                const newQty = isNaN(parsed) ? 0 : Math.max(0, parsed);
                                e.target.value = String(newQty);
                                if (newQty !== item.quantity) {
                                  updateInvoiceItemMutation.mutate({
                                    itemId: item.id,
                                    updates: { quantity: newQty }
                                  });
                                }
                              }}
                              className={`w-14 h-9 text-sm font-semibold text-center border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                                item.chargeType === 'not_applicable' || lineItemsLocked ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-50'
                              }`}
                              data-testid={`input-quantity-${item.id}`}
                            />
                          ) : (
                            <span className="text-sm font-semibold text-gray-900">{item.quantity ?? 0}</span>
                          )}
                        </div>

                        {/* Total Column */}
                        <div className="col-span-2 text-right">
                          <span className="font-semibold text-gray-900">
                            {item.unitAmount 
                              ? (parseFloat(item.unitAmount) * (item.quantity ?? 0)).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                              : '—'}
                          </span>
                        </div>

                        {/* Charge Type Column */}
                        <div className="col-span-2 flex items-center justify-center">
                          {canEditStatus ? (
                            <Select
                              value={item.chargeType || 'include_in_invoice'}
                              disabled={lineItemsLocked}
                              onValueChange={(value) => {
                                const updates: any = { chargeType: value };
                                if (value === 'not_applicable') {
                                  updates.quantity = 0;
                                }
                                updateInvoiceItemMutation.mutate({
                                  itemId: item.id,
                                  updates,
                                });
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs w-full" data-testid={`select-charge-type-${item.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="include_in_invoice">Include in invoice</SelectItem>
                                <SelectItem value="paid_with_client_card">Paid with client card</SelectItem>
                                <SelectItem value="not_applicable">Not applicable</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-xs text-gray-600">
                              {item.chargeType === 'include_in_invoice' ? 'Include in invoice' :
                               item.chargeType === 'paid_with_client_card' ? 'Paid with client card' :
                               item.chargeType === 'not_applicable' ? 'Not applicable' : item.chargeType}
                            </span>
                          )}
                        </div>

                        {/* Actions Column */}
                        <div className="col-span-1 flex items-center justify-end">
                          {canEditStatus && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-gray-400 hover:text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:pointer-events-none"
                              disabled={lineItemsLocked}
                              onClick={() => {
                                if (confirm("Remove this item from the invoice?")) {
                                  deleteInvoiceItemMutation.mutate(item.id);
                                }
                              }}
                              data-testid={`button-delete-invoice-item-${item.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Client Comments Row */}
                      {canEditStatus ? (
                        <div className="px-4 pb-3">
                          <input
                            type="text"
                            defaultValue={item.clientComments || ""}
                            disabled={lineItemsLocked}
                            onBlur={(e) => {
                              const newComments = e.target.value || null;
                              if (newComments !== (item.clientComments || null)) {
                                updateInvoiceItemMutation.mutate({
                                  itemId: item.id,
                                  updates: { clientComments: newComments }
                                });
                              }
                            }}
                            placeholder="Add client comments (visible on invoice)..."
                            className="w-full h-8 px-3 text-sm bg-gray-50/50 border border-dashed border-gray-200 rounded-md placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary focus:border-solid disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100"
                            data-testid={`input-comments-${item.id}`}
                          />
                        </div>
                      ) : item.clientComments ? (
                        <div className="px-4 pb-3">
                          <p className="text-sm text-gray-600">{item.clientComments}</p>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>

                {/* Invoice Total */}
                <div className="border-t pt-4 mt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">Invoice Subtotal</span>
                    <span className="text-xl font-bold text-gray-900" data-testid="text-invoice-subtotal">
                      {parseFloat(invoiceData.total).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                    </span>
                  </div>
                  {paymentStatus && paymentStatus.status !== 'draft' && (
                    <>
                      {paymentStatus.amountPaid && parseFloat(paymentStatus.amountPaid) > 0 && (
                        <div className="flex items-center justify-between text-green-700">
                          <span className="text-sm">Amount Paid</span>
                          <span className="font-semibold" data-testid="text-amount-paid">
                            -{parseFloat(paymentStatus.amountPaid).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                          </span>
                        </div>
                      )}
                      {paymentStatus.unpaidBalance && parseFloat(paymentStatus.unpaidBalance) > 0 && (
                        <div className="flex items-center justify-between text-red-700 border-t pt-2">
                          <span className="text-base font-semibold">Balance Due</span>
                          <span className="text-lg font-bold" data-testid="text-balance-due">
                            {parseFloat(paymentStatus.unpaidBalance).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                  {parseFloat(invoiceData.paidWithClientCardTotal) > 0 && (
                    <div className="flex items-center justify-between text-amber-700">
                      <span className="text-sm">Paid with client card</span>
                      <span className="font-semibold" data-testid="text-paid-client-card-total">
                        {parseFloat(invoiceData.paidWithClientCardTotal).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                      </span>
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Subtotal includes only "Include in invoice" items
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Receipt className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No invoice items for this work order.</p>
                <p className="text-sm mt-2">Generate default invoice items based on the service type and applicable pass-through fees.</p>
                {canEditStatus && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    disabled={lineItemsLocked || createDefaultInvoiceItemsMutation.isPending}
                    onClick={() => createDefaultInvoiceItemsMutation.mutate()}
                    data-testid="button-generate-invoice-items"
                  >
                    {createDefaultInvoiceItemsMutation.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Plus className="w-4 h-4 mr-2" />
                    )}
                    Generate Invoice Items
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Status History Timeline */}
        {statusHistory && statusHistory.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-lg">
                <History className="w-5 h-5" />
                Status History
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                {[...statusHistory].reverse().map((entry: any) => {
                  const changedAt = new Date(entry.changedAt);
                  const userName = formatWorkOrderStatusChangedBy(entry.changedByUser);
                  return (
                    <div key={entry.id} className="flex items-center gap-3 text-sm">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        {entry.fromStatus ? (
                          <>
                            <Badge className={`${getStatusColor(entry.fromStatus)} border-0 text-xs shrink-0`}>
                              {formatStatus(entry.fromStatus)}
                            </Badge>
                            <ArrowRight className="w-3 h-3 text-gray-400 shrink-0" />
                          </>
                        ) : (
                          <span className="text-xs text-gray-400 italic shrink-0">Created as</span>
                        )}
                        <Badge className={`${getStatusColor(entry.toStatus)} border-0 text-xs shrink-0`}>
                          {formatStatus(entry.toStatus)}
                        </Badge>
                      </div>
                      <span className="text-xs text-gray-500 shrink-0">
                        {changedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {" "}
                        {changedAt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                        {" "}by {userName}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Documents Section - Bottom */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileArchive className="w-5 h-5" />
                Documents
              </CardTitle>
              <Badge variant="outline">Coming Soon</Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              <FileArchive className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Document management functionality will be available soon.</p>
              <p className="text-sm mt-2">This will include document uploads, version control, and sharing.</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Company Details Modal */}
      {showCompanyDetails && workOrder.company && (
        <CompanyDetails
          open={showCompanyDetails}
          onClose={() => setShowCompanyDetails(false)}
          companyId={workOrder.company.id}
        />
      )}

      {/* Carrier Details Modal */}
      {showCarrierDetails && workOrder.carrier && (
        <CarrierDetails
          open={showCarrierDetails}
          onClose={() => setShowCarrierDetails(false)}
          carrierId={workOrder.carrier.id}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <EditWorkOrderModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdateWorkOrder}
          isLoading={updateWorkOrderMutation.isPending}
          workOrder={workOrder}
          users={users || []}
          paymentStatus={paymentStatus}
        />
      )}

      {/* Create FMCSA Incident Modal */}
      <CreateFmcsaIncidentModal
        open={showCreateIncidentModal}
        onOpenChange={(open) => {
          setShowCreateIncidentModal(open);
          if (!open) {
            queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/fmcsa-incidents`] });
          }
        }}
        prePopulatedData={{
          companyId: workOrder?.companyId,
          workOrderIds: [workOrderId]
        }}
      />

      {/* Edit FMCSA Incident Modal */}
      {selectedIncident && (
        <EditFmcsaIncidentModal
          open={showEditIncidentModal}
          onOpenChange={(open) => {
            setShowEditIncidentModal(open);
            if (!open) {
              setSelectedIncident(null);
              queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/fmcsa-incidents`] });
            }
          }}
          incident={selectedIncident}
        />
      )}

      {/* Authority Letters Search Modal */}
      <AuthorityLettersSearchModal
        open={showAuthorityLettersSearch}
        onClose={() => setShowAuthorityLettersSearch(false)}
        workOrderId={workOrderId}
      />

      {/* Watchlist Create Modal */}
      <WatchlistCreateModal
        open={showWatchlistCreate}
        onClose={() => setShowWatchlistCreate(false)}
        workOrderId={workOrderId}
        workOrder={workOrder}
      />

      {/* Watchlist Detail Modal */}
      <WatchlistDetailModal
        watchlist={selectedWatchlist}
        open={showWatchlistDetail}
        onOpenChange={setShowWatchlistDetail}
      />

      {/* Payment Collection Modal */}
      {showPaymentCollectionModal && invoiceData && (
        <PaymentCollectionModal
          open={showPaymentCollectionModal}
          onClose={() => setShowPaymentCollectionModal(false)}
          workOrderId={workOrderId}
          workOrderNumber={workOrder.workOrderNumber}
          companyName={workOrder.company?.name || "Unknown Company"}
          companyEmail={workOrder.company?.email ?? undefined}
          contactEmail={workOrder.requestingContact?.email ?? undefined}
          contactPhone={workOrder.requestingContact?.phone ?? undefined}
          totalAmount={parseFloat(invoiceData.total) || 0}
          lineItems={invoiceData.lineItems.map((item: any) => ({
            description: item.description,
            amount: parseFloat(item.unitAmount) || 0,
            quantity: item.quantity ?? 0,
          }))}
        />
      )}
      {/* Stripe Invoice History Modal */}
      {showInvoiceHistory && paymentStatus?.stripeInvoiceId && (
        <StripeInvoiceHistoryModal
          open={showInvoiceHistory}
          onOpenChange={setShowInvoiceHistory}
          stripeInvoiceId={paymentStatus.stripeInvoiceId}
          label={paymentStatus.stripeInvoiceNumber
            ? `#${paymentStatus.stripeInvoiceNumber}`
            : paymentStatus.batchInvoiceRunId != null
            ? `Batch #${paymentStatus.batchInvoiceRunId}`
            : undefined}
          batchRunId={paymentStatus.batchInvoiceRunId ?? undefined}
        />
      )}
    </ResponsiveLayout>
  );
}
